import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useWebSocket } from "../contexts/WebSocketContext";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../components/ui/breadcrumb";
import { Checkbox } from "../components/ui/checkbox";
import { AlertCircle, CheckCircle2, Clock, AlertTriangle, Search, RefreshCw, Loader2, FileX, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  success: { icon: CheckCircle2, className: "status-success", label: "Success" },
  warning: { icon: AlertTriangle, className: "status-warning", label: "Warning" },
  error: { icon: AlertCircle, className: "status-error", label: "Error" },
  pending: { icon: Clock, className: "status-pending", label: "Pending" }
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { connect, disconnect, lastMessage } = useWebSocket();
  
  const [project, setProject] = useState(null);
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [envFilter, setEnvFilter] = useState("all");
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const fetchProject = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/projects/${projectId}`, { withCredentials: true });
      setProject(data);
    } catch (error) {
      toast.error("Project not found");
      navigate("/dashboard");
    }
  }, [projectId, navigate]);

  const fetchTraces = useCallback(async () => {
    try {
      let url = `${API_URL}/api/v1/traces?project_id=${projectId}`;
      if (envFilter !== "all") {
        url += `&environment=${envFilter}`;
      }
      const { data } = await axios.get(url, { withCredentials: true });
      setTraces(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load traces");
      setTraces([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [projectId, envFilter]);

  useEffect(() => {
    fetchProject();
    fetchTraces();
    connect(projectId);

    return () => {
      disconnect();
    };
  }, [projectId, fetchProject, fetchTraces, connect, disconnect]);

  useEffect(() => {
    fetchTraces();
  }, [envFilter, fetchTraces]);

  // Handle WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      if (lastMessage.type === "new_trace") {
        toast.info("New trace received");
        fetchTraces();
      } else if (lastMessage.type === "trace_evaluated") {
        setTraces((prev) =>
          prev.map((t) =>
            t.id === lastMessage.trace_id
              ? { ...t, status: lastMessage.status, evaluation: lastMessage.evaluation }
              : t
          )
        );
      }
    }
  }, [lastMessage, fetchTraces]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTraces();
  };

  const toggleCompare = (traceId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(traceId)) return prev.filter(id => id !== traceId);
      if (prev.length >= 2) {
        toast.info("You can only compare 2 traces at a time");
        return prev;
      }
      return [...prev, traceId];
    });
  };

  const handleCompare = () => {
    if (selectedForCompare.length !== 2) {
      toast.error("Select exactly 2 traces to compare");
      return;
    }
    navigate(`/compare?a=${selectedForCompare[0]}&b=${selectedForCompare[1]}&project=${projectId}`);
  };

  const filteredTraces = traces.filter((trace) => {
    if (!searchQuery) return true;
    return trace.user_query?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.className} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getIssueCount = (evaluation) => {
    if (!evaluation?.issues) return 0;
    return evaluation.issues.length;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="project-detail-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{project?.name || "Loading..."}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Project Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-black tracking-tight font-heading" data-testid="project-name">
              {project?.name}
            </h1>
            <p className="text-muted-foreground">
              {project?.description || "No description"}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-muted-foreground">Project ID:</span>
              <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono" data-testid="project-id">
                {projectId}
              </code>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2"
                onClick={() => {
                  navigator.clipboard.writeText(projectId);
                  toast.success("Project ID copied!");
                }}
              >
                <span className="text-xs">Copy</span>
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedForCompare.length > 0 && (
              <Button
                variant={selectedForCompare.length === 2 ? "default" : "outline"}
                size="sm"
                onClick={handleCompare}
                disabled={selectedForCompare.length !== 2}
                data-testid="compare-button"
              >
                <GitCompareArrows className="w-4 h-4 mr-2" />
                Compare ({selectedForCompare.length}/2)
              </Button>
            )}
            {selectedForCompare.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedForCompare([])} data-testid="clear-compare-button">
                Clear
              </Button>
            )}
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-traces-button">
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search queries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-traces-input"
            />
          </div>
          <Select value={envFilter} onValueChange={setEnvFilter}>
            <SelectTrigger className="w-[180px]" data-testid="env-filter-select">
              <SelectValue placeholder="Environment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Environments</SelectItem>
              <SelectItem value="development">Development</SelectItem>
              <SelectItem value="staging">Staging</SelectItem>
              <SelectItem value="production">Production</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Traces Table */}
        {loading ? (
          <Card className="border border-border shadow-none">
            <CardContent className="py-8">
              <div className="flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ) : filteredTraces.length === 0 ? (
          <Card className="border border-border shadow-none" data-testid="empty-traces">
            <CardContent className="py-12 text-center">
              <FileX className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No traces yet</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery 
                  ? "No traces match your search"
                  : "Start sending traces from your RAG application using the SDK"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-border shadow-none overflow-hidden" data-testid="traces-table-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-[40%]">Query</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issues</TableHead>
                  <TableHead>Environment</TableHead>
                  <TableHead>Latency</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTraces.map((trace) => (
                  <TableRow 
                    key={trace.id} 
                    className={`cursor-pointer hover:bg-muted/50 ${selectedForCompare.includes(trace.id) ? 'bg-blue-50/60' : ''}`}
                    data-testid={`trace-row-${trace.id}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedForCompare.includes(trace.id)}
                        onCheckedChange={() => toggleCompare(trace.id)}
                        data-testid={`compare-checkbox-${trace.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium" onClick={() => navigate(`/traces/${trace.id}`)}>
                      <span className="line-clamp-1">{trace.user_query}</span>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/traces/${trace.id}`)}>{getStatusBadge(trace.status)}</TableCell>
                    <TableCell onClick={() => navigate(`/traces/${trace.id}`)}>
                      {getIssueCount(trace.evaluation) > 0 ? (
                        <Badge variant="destructive" className="font-mono">
                          {getIssueCount(trace.evaluation)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell onClick={() => navigate(`/traces/${trace.id}`)}>
                      <Badge variant="outline" className="font-mono text-xs">
                        {trace.environment}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm" onClick={() => navigate(`/traces/${trace.id}`)}>
                      {trace.latency_ms}ms
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm" onClick={() => navigate(`/traces/${trace.id}`)}>
                      {formatDistanceToNow(new Date(trace.created_at), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </main>
    </div>
  );
}
